import React, { useMemo, useState } from "react";
import { ref, set, push, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { X, DollarSign } from "lucide-react";

const ClientPaymentModal = ({ client, companyName, onClose }) => {
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [transactionRef, setTransactionRef] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  // when true, Credits require a reason in the notes field; otherwise notes are optional
  const [requireReasonForCredit, setRequireReasonForCredit] = useState(true);
  const [loading, setLoading] = useState(false);

  if (!client) return null;

  const formatKES = (v) => {
    try {
      return Number(v).toLocaleString();
    } catch {
      return v;
    }
  };

  const doRecordPayment = async (paymentAmount, isCredit, paymentDateValue) => {
    setLoading(true);
    try {
      const currentBalance = parseFloat(client.balance) || 0;
      // Credit increases client's debt (balance + amount), normal payment reduces balance
      const newBalance = isCredit ? currentBalance - paymentAmount : currentBalance + paymentAmount;

      // Create payment record meta
      const paymentId = push(ref(database, "temp")).key;
      const paymentDateObj = new Date(paymentDateValue + "T00:00:00");
      const paymentDate = paymentDateObj.toISOString();
      const recordedAt = new Date().toISOString();
      const year = paymentDateObj.getFullYear();
      const month = paymentDateObj.toLocaleString("en-US", { month: "long" });

      const paymentData = {
        paymentId,
        clientId: client.id,
        clientName: client.name,
        amount: paymentAmount,
        paymentMethod,
        transactionRef,
        notes, // use notes as the reason when Credit
        paymentDate,
        recordedAt,
        status: "Completed",
        recordedBy: companyName,
      };

      // Save payment entry
      await set(
        ref(
          database,
          `Companies/${companyName}/ClientPayments/${client.id}/payments/${year}/${month}/${paymentId}`
        ),
        paymentData
      );

      // Update client balances and accounts
      try {
        await set(
          ref(database, `Companies/${companyName}/ValidatedClients/${client.id}/Balance`),
          newBalance
        );

        await set(
          ref(database, `Companies/${companyName}/AIValidatedClients/${client.id}/Balance`),
          newBalance
        );

        // Update shops_receivable balance
        const shopsReceivableRef = ref(database, `Companies/${companyName}/AccountBalances/shop_receivable`);
        const shopsReceivableSnap = await get(shopsReceivableRef);
        const currentShopsReceivable = shopsReceivableSnap.exists()
          ? (parseFloat(shopsReceivableSnap.val().balance) || 0)
          : 0;

        // Accounting directions:
        // - Normal payment: receivable decreases -> "credit"
        // - Credit: receivable increases -> "debit"
        const newShopsReceivable = isCredit
          ? currentShopsReceivable + paymentAmount
          : currentShopsReceivable - paymentAmount;

        await set(shopsReceivableRef, {
          balance: newShopsReceivable,
          lastUpdated: new Date().toISOString(),
        });

        // Update company account (cash or bank) only for real payments (not Credit).
        if (!isCredit) {
          const accountType = paymentMethod === "Cash" ? "company_cash" : "company_bank";
          const accountRef = ref(database, `Companies/${companyName}/AccountBalances/${accountType}`);
          const accountSnap = await get(accountRef);
          const currentAccountBalance = accountSnap.exists()
            ? (parseFloat(accountSnap.val().balance) || 0)
            : 0;
          const newAccountBalance = currentAccountBalance + paymentAmount;

          await set(accountRef, {
            balance: newAccountBalance,
            lastUpdated: new Date().toISOString(),
          });
        }

        // Record transaction in shops_receivable ledger
        const transactionId = push(ref(database, "temp")).key;
        const shopsReceivableTransPath = `Companies/${companyName}/Accounts/shop_receivable/${year}/${month}/${transactionId}`;
        await set(ref(database, shopsReceivableTransPath), {
          id: transactionId,
          date: paymentDate,
          amount: paymentAmount,
          // direction matches change in receivables:
          direction: isCredit ? "debit" : "credit",
          type: isCredit ? "credit" : "client_payment",
          clientId: client.id,
          clientName: client.name,
          paymentMethod,
          // use notes as reason when isCredit; otherwise normal notes/payment text
          notes: isCredit
            ? (notes ? `${notes}` : `Credit for ${client.name}`)
            : (notes || `Payment received from ${client.name}`),
          reference: paymentId,
          transactionRef,
        });

        // Record transaction in company account ledger for real payments.
        if (!isCredit) {
          const accountType = paymentMethod === "Cash" ? "company_cash" : "company_bank";
          const accountTransId = push(ref(database, "temp")).key;
          const accountTransPath = `Companies/${companyName}/Accounts/${accountType}/${year}/${month}/${accountTransId}`;

          // Asset increase should be recorded as "debit" in accounting
          await set(ref(database, accountTransPath), {
            id: accountTransId,
            date: paymentDate,
            amount: paymentAmount,
            direction: "debit",
            type: "client_payment",
            clientId: client.id,
            clientName: client.name,
            paymentMethod,
            notes: notes || `Payment from ${client.name}`,
            reference: paymentId,
            transactionRef,
          });
        }
      } catch (balanceError) {
        console.error("Error updating balances/accounts:", balanceError);
        toast.warning("Payment recorded but balance/account update may have failed. Please verify.");
      }

      toast.success(`Payment of KES ${formatKES(paymentAmount)} recorded successfully`);
      onClose();
    } catch (error) {
      console.error("Error recording payment:", error);
      toast.error("Failed to record payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentAmount = parseFloat(amount);
    if (!amount || isNaN(paymentAmount) || paymentAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (selectedDate > today) {
      toast.error("Payment date cannot be in the future");
      return;
    }

    const isCredit = paymentMethod === "Credit";

    // If Credit is selected and the boolean requireReasonForCredit is true,
    // require notes to be non-empty (notes act as the reason).
    if (isCredit && requireReasonForCredit) {
      if (!notes || notes.trim() === "") {
        toast.error("Please provide a reason in the Notes field for the Credit");
        return;
      }
    }

    // Proceed to record (notes used as reason if Credit)
    await doRecordPayment(paymentAmount, isCredit, selectedDate);
  };

  // Preview new balance in UI
  const currentBalance = parseFloat(client.balance) || 0;
  const previewAmount = parseFloat(amount || 0);
  const previewNewBalance = paymentMethod === "Credit"
    ? currentBalance - (isNaN(previewAmount) ? 0 : previewAmount)
    : currentBalance + (isNaN(previewAmount) ? 0 : previewAmount);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-cyan-700 flex items-center gap-2">
            <DollarSign size={24} />
            Record Client Payment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={loading}
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-4 p-3 bg-cyan-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Client:</strong> {client.name}
          </p>
          <p className="text-sm text-gray-700">
            <strong>Current Balance:</strong> KES {formatKES(currentBalance)}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date *
              </label>
              <input
                type="date"
                value={selectedDate}
                max={today}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Amount (KES) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter amount"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                required
              >
                <option value="Cash">Cash</option>
                <option value="M-Pesa">M-Pesa</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
                <option value="Credit">Credit</option>
              </select>
            </div>

            {paymentMethod === "Credit" && (
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (used as reason for Credit){requireReasonForCredit ? " *" : " (optional)"}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    rows="3"
                    placeholder="Enter reason for Credit (e.g., returned items, billing correction) — this will be saved in Notes"
                    required={requireReasonForCredit}
                  />
                </div>

                <div className="w-36 flex-shrink-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Require reason
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="require-reason"
                      type="checkbox"
                      checked={requireReasonForCredit}
                      onChange={(e) => setRequireReasonForCredit(!!e.target.checked)}
                      className="h-4 w-4"
                    />
                    <label htmlFor="require-reason" className="text-sm text-gray-600">
                      Enforce
                    </label>
                  </div>
                </div>
              </div>
            )}

            {paymentMethod !== "Credit" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  rows="3"
                  placeholder="Any additional notes..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transaction Reference (Optional)
              </label>
              <input
                type="text"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="e.g., M-Pesa code, cheque number"
              />
            </div>

            {amount && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>New Balance:</strong> KES {formatKES(previewNewBalance)}
                </p>
                {paymentMethod === "Credit" ? (
                  <p className="text-xs text-gray-600 mt-1">
                    Note: Recording a Credit will increase the client's debt. This will
                    create a "credit" transaction and increase the shop receivables balance.
                    The Notes field will be used as the reason.
                  </p>
                ) : (
                  <p className="text-xs text-gray-600 mt-1">
                    Note: Recording a payment will decrease the client's balance and
                    increase the company's cash/bank balance.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:bg-cyan-300"
              disabled={loading}
            >
              {loading ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientPaymentModal;
